import {Button, Col, Divider, Popover, Row} from 'antd';
import {FilterFilled} from '@ant-design/icons';
import _, {capitalize} from 'lodash';
import {Trans, useTranslation} from 'react-i18next';
import {
  addFilters,
  removeFilters,
  removeOverallLandCoverageFilter,
  removeOverallLandCoverageMaxFilter,
  removeOverallLandCoverageMinFilter,
  selectAssetsTableFilters,
  setOverallLandCoverageMaxFilter,
  setOverallLandCoverageMinFilter,
  setOverallLandCoverageNullFilter
} from '../../../redux/slices/assets';
import {useDispatch, useSelector} from 'react-redux';
import {
  AssetsTableColumn,
  getFiltersOfTheAssetsTableStaticColumn,
  isAssetsTableStaticColumn,
} from '../../../types/internal/tables/assetsTable';
import DatesFilter from '../../common/filters/DatesFilter';
import NumbersFilter from '../../common/filters/NumbersFilter';
import StandardFilter from '../../common/filters/StandardFilter';
import {BIODIVERSITY_WITHIN} from "../../../types/api/assets";
import {VALUE_NOT_PRESENT} from "../../../utils/translation";
import React from "react";
import AssetRelatedOptionsFilter from "./filters/AssetRelatedOptionsFilter";
import HazardFilter from "./filters/HazardFilter";
import {useGetHazardTypeClassesQuery} from "../../../redux/api/hazardTypes";
import {useGetAssetBiodiversityDataSourcesQuery} from "../../../redux/api/asset";
import {useGetAlertParametersTypeQuery} from "../../../redux/api/alertParameters";
import SingleNumberFilter from "../../common/filters/SingleNumberFilter";
import {TFunction} from "i18next";
import BiodiversitySourceFilter from "./filters/BiodiversitySourceFilter";
import BiodiversityDistance from "./filters/BiodiversityDistance";

export function getLabelForAlertParameterFilters(limitType: string | undefined, t : TFunction){
  let displayText: string
      switch (limitType) {
        case "lt":
          displayText = capitalize(t('misc.lowerThan'))
          break
        case "lte":
          displayText = capitalize(t('misc.lowerThanEqual'))
          break
        case "gt":
          displayText = capitalize(t('misc.greaterThan'))
          break
        case "gte":
          displayText = capitalize(t('misc.greaterThanEqual'))
          break
        default:
          displayText = ""
      }
      return displayText
}

export default function AssetsTableFilter({column}: {
  column: Exclude<AssetsTableColumn, 'map_view'>,
}) {

  const {t} = useTranslation();
  const dispatch = useDispatch();
  const activeFilters = useSelector(selectAssetsTableFilters);

  const {
    data: hazardTypes,
  } = useGetHazardTypeClassesQuery();
  const {
    data: biodiversitySources,
  } = useGetAssetBiodiversityDataSourcesQuery()
  const {
    data: alertParametersTypeSources
  } = useGetAlertParametersTypeQuery({paginate: false})

  const isHazardFilter = hazardTypes?.find(ht => ht.code === column);
  const isBioSourceFilter = biodiversitySources?.find(bs => bs.value === column)
  const isAlertParameterFilter = alertParametersTypeSources?.find(param => param.code === column)

  let filter = <></>;
  if (isAssetsTableStaticColumn(column)) {
    switch (column) {
      case 'biodiversity_within':
        filter = <div style={{ maxWidth: '30rem' }}>
          <BiodiversityDistance/>
          <BiodiversitySourceFilter/>
          <Divider/>
          <Trans
            i18nKey={'assets.landCoverageDescription'} components={{li: <li style={{fontStyle: 'italic'}}/>, ul: <ul style={{fontStyle: 'italic'}} />, p: <p style={{fontStyle: 'italic'}}/>}}
          />
          <StandardFilter
            selectedFilters={activeFilters.biodiversity_within}
            filters={[
             {label: capitalize(t('assets.landCoverageIn')), value: BIODIVERSITY_WITHIN.IN},
              {label: capitalize(t('assets.landCoverageOut')), value: BIODIVERSITY_WITHIN.OUT},
              {label: VALUE_NOT_PRESENT, value: BIODIVERSITY_WITHIN.NA},
            ]}
            onChange={(values) => {
              if (values.length) {
                dispatch(addFilters({biodiversity_within: values}));
              } else {
                dispatch(removeFilters('biodiversity_within'));
              }
            }}
          />
        </div>
        break;
      case 'surface_basement':
      case 'surface_total':
      case 'total_resources':
        filter = <NumbersFilter
          numericProps={{
            suffix: (column !== 'total_resources') ? ' mq' : '',
          }}
          onNullChange={(v) => {
            if (v) {
              dispatch(addFilters({[`is_${column}_null`]: v}));
              dispatch(removeFilters([`${column}_min`, `${column}_max`]));
            } else {
              dispatch(removeFilters(`is_${column}_null`));
            }
          }}
          onMinChange={(value) => {
            if (typeof value === 'number') {
              dispatch(addFilters({[`${column}_min`]: value}));
            } else {
              dispatch(removeFilters(`${column}_min`));
            }
          }}
          onMaxChange={(value) => {
            if (typeof value === 'number') {
              dispatch(addFilters({[`${column}_max`]: value}));
            } else {
              dispatch(removeFilters(`${column}_max`));
            }
          }}
          min={activeFilters[`${column}_min`]}
          max={activeFilters[`${column}_max`]}
          isNull={activeFilters[`is_${column}_null`]}
        />;
        break;
      case 'total_uog_contacts':
        filter = <NumbersFilter
          onMinChange={(value) => {
            if (typeof value === 'number') {
              dispatch(addFilters({[`${column}_min`]: value}));
            } else {
              dispatch(removeFilters(`${column}_min`));
            }
          }}
          onMaxChange={(value) => {
            if (typeof value === 'number') {
              dispatch(addFilters({[`${column}_max`]: value}));
            } else {
              dispatch(removeFilters(`${column}_max`));
            }
          }}
          min={activeFilters[`${column}_min`]}
          max={activeFilters[`${column}_max`]}
          isNullable={false}
        />;
        break;
      case 'opening_date':
      case 'closing_date':
        filter = <DatesFilter
          isNull={activeFilters[`is_${column}_null`]}
          start={activeFilters[`${column}_after`]}
          end={activeFilters[`${column}_before`]}
          onNullChange={(value) => {
            if (value) {
              dispatch(addFilters({[`is_${column}_null`]: value}));
              dispatch(removeFilters([`${column}_after`, `${column}_before`]));
            } else {
              dispatch(removeFilters(`is_${column}_null`));
            }
          }}
          onStartChange={(value) => {
            if (value) {
              dispatch(addFilters({[`${column}_after`]: value}));
            } else {
              dispatch(removeFilters(`${column}_after`));
            }
          }}
          onEndChange={(value) => {
            if (value) {
              dispatch(addFilters({[`${column}_before`]: value}));
            } else {
              dispatch(removeFilters(`${column}_before`));
            }
          }}
        />;
        break;
      case 'acoustic_zone':
      case 'address':
      case 'admin_level_1':
      case 'admin_level_2':
      case 'admin_level_3':
      case 'building_type':
      case 'building_type_detail':
      case 'companies':
      case 'contact_people':
      case 'country':
      case 'is_user_created':
      case 'master_asset':
      case 'ownership':
      case 'postal_code':
      case 'realestate_hub':
      case 'realestate_garrison':
      case 'sigi_code':
        filter = <AssetRelatedOptionsFilter field={column}/>;
        break;
      default:
        filter = <>
          WORK IN PROGRESS...
        </>;
    }
  } else {
    if (isHazardFilter) {
      filter = <HazardFilter column={column}/>;
    }
    if (isAlertParameterFilter) {
      const suffix = alertParametersTypeSources?.find(param => param.code === column)?.um
      const limitType = alertParametersTypeSources?.find(param => param.code === column)?.limit_type
      const decimalScale = alertParametersTypeSources?.find(param => param.code === column)?.decimals
      const displayText = getLabelForAlertParameterFilters(limitType, t)
      filter = <SingleNumberFilter
        activeValue={activeFilters?.alert_parameters?.[column]}
        suffix={suffix ?? ""}
        displayText={displayText}
        decimalScale={decimalScale}
        onChange={value => {
          if (typeof value === 'number') {
            dispatch(addFilters({alert_parameters: {[column]: value}}));
          } else {
            dispatch(removeFilters(column));
          }
        }}
      />
    }
    if (isBioSourceFilter) {
      filter = <NumbersFilter
        numericProps={{
          suffix: ' %',
          allowNegative: false,
        }}
        onNullChange={(v) => {
            if (v) {
              dispatch(setOverallLandCoverageNullFilter({source: column}))
            } else {
              dispatch(removeOverallLandCoverageFilter(column));
            }
          }}
        onMinChange={(value) => {
          if (typeof value === 'number') {
            dispatch(setOverallLandCoverageMinFilter({source: column, value}))
          } else {
            dispatch(removeOverallLandCoverageMinFilter(column));
          }
        }}

        onMaxChange={(value) => {
          if (typeof value === 'number') {
            dispatch(setOverallLandCoverageMaxFilter({source: column, value}))
          } else {
            dispatch(removeOverallLandCoverageMaxFilter(column));
          }
        }}

        min={activeFilters?.overall_land_coverage?.[column]?.min}
        max={activeFilters?.overall_land_coverage?.[column]?.max}
        isNull={activeFilters?.overall_land_coverage?.[column]?.is_null}
      />;
    }

  }

  const isTotalFilterActive = (column: AssetsTableColumn) => {
    if (isAssetsTableStaticColumn(column)) {
      return getFiltersOfTheAssetsTableStaticColumn(column).some(filter => !_.isUndefined(activeFilters[filter]));
    } else {
      return !_.isUndefined(activeFilters.hazards?.[column]) || !_.isUndefined(activeFilters.alert_parameters?.[column]) || !_.isUndefined(activeFilters.overall_land_coverage?.[column]);
    }
  };

  return <Popover
    arrow={false}
    trigger={'click'}
    placement={'bottom'}
    destroyTooltipOnHide
    content={(
      <>
        {filter}
        <Divider/>
        <Row justify={'end'}>
          <Col>
            <Button
              disabled={!isTotalFilterActive(column)}
              type={'link'}
              onClick={() => {
                if (isAssetsTableStaticColumn(column)) {
                  dispatch(removeFilters(getFiltersOfTheAssetsTableStaticColumn(column)));
                } else {
                  if (isHazardFilter) {
                    dispatch(removeFilters(column));
                  }
                  if (isAlertParameterFilter) {
                    dispatch(removeFilters(column))
                  }
                  if (isBioSourceFilter) {
                    dispatch(removeOverallLandCoverageFilter(column));
                  }
                }
              }}
            >
              {capitalize(t('table.removeFilter'))}
            </Button>
          </Col>
        </Row>
      </>
    )}
  >
    <FilterFilled
      style={{
        opacity: '25%',
        ...{
          ...isTotalFilterActive(column) && {color: '#1677ff', opacity: ''},
        },
      }}
    />
  </Popover>;
}